import { Info, ThumbsDown } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { Skeleton } from "@/components/skeleton";

interface AiSuggestionsProps {
  conversationId: string;
  onSelectSuggestion: (text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => void;
  onRejectSuggestion?: (text: string, meta: { suggestionIndex: number; aiGenerated: boolean }) => void;
}

interface AiSuggestionsResponse {
  suggestions: string[];
  aiGenerated: boolean;
}

/**
 * AI Response Suggestions panel.
 * Fetches suggestions from the inbox API which either returns AI-generated
 * replies or server-side fallback suggestions. Only renders when the backend
 * actually returns suggestions — no client-side fakes.
 * EU AI Act Art. 52: labelled as AI-generated only when aiGenerated === true.
 */
export function AiSuggestions({ conversationId, onSelectSuggestion, onRejectSuggestion }: AiSuggestionsProps) {
  const { data, isLoading: loading, error } = useApi<AiSuggestionsResponse>(
    conversationId ? `/v1/inbox/conversations/${conversationId}/suggestions` : "",
    { enabled: !!conversationId },
  );

  if (loading) {
    return (
      <div className="px-4 py-2 border-t border-[var(--color-border)]">
        <div className="space-y-2">
          <Skeleton className="h-3 w-32" />
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-9 w-full rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  const suggestions = data?.suggestions ?? [];
  const isAiGenerated = data?.aiGenerated === true;

  if (error || suggestions.length === 0) return null;

  return (
    <div className="px-4 py-2 border-t border-[var(--color-border)] bg-[var(--color-primary)]/50 dark:bg-[var(--color-primary)]/10">
      <div className="flex items-center gap-1 mb-1.5">
        <span className="text-[10px] font-medium text-[var(--color-primary)] dark:text-[var(--color-primary)]/70 bg-[var(--color-primary)]/10 dark:bg-[var(--color-primary)]/30 px-1.5 py-0.5 rounded">
          {/* EU AI Act Art. 52: only label as AI-generated when the API confirms it */}
          {isAiGenerated ? "AI-suggested replies" : "Suggested replies"}
        </span>
        {isAiGenerated && (
          <span className="text-[var(--color-muted-foreground)]" title="AI-generated suggestions based on conversation context">
            <Info className="w-3 h-3" />
          </span>
        )}
      </div>
      <div className="space-y-1">
        {suggestions.map((text, index) => (
          <div key={index} className="stagger-item flex items-stretch gap-2">
            <button
              onClick={() => onSelectSuggestion(text, { suggestionIndex: index, aiGenerated: isAiGenerated })}
              className="flex-1 text-left px-3 py-1.5 text-sm rounded-md border border-[var(--color-primary)]/20 dark:border-[var(--color-primary)]/90 hover:bg-[var(--color-primary)]/10 dark:hover:bg-[var(--color-primary)]/30 transition-colors text-[var(--color-foreground)]"
            >
              {text}
            </button>
            <button
              type="button"
              onClick={() => onRejectSuggestion?.(text, { suggestionIndex: index, aiGenerated: isAiGenerated })}
              className="rounded-md border border-[var(--color-border)] px-2 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
              aria-label={`Dismiss suggestion ${index + 1}`}
              title="Dismiss suggestion"
            >
              <ThumbsDown className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
